import { useMemo, useRef, useState } from "react";
import { Button, Field, Input, Select } from "@fluentui/react-components";
import { Add20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import StopCodingList from "./StopCodingList";
import { useStopCodingSelector } from "../OI/redux/ReduxContext";
import {
    filterStopCausesForCascade,
    getStopReasonDrilldownGroupPath,
    getStopReasonSelectionPath,
    isStopReasonInCascadePath,
    normalizeStopCodingReason
} from "./stopCodingCascadeUtils";
import { StopCause } from "../../Types/StopCause";
import CustomSelect from "../OI/CustomSelect";

type FilterMode = 'include' | 'exclude';
type FilterField = 'machine' | 'alias' | 'catalogueCode' | 'reason' | 'mwc' | 'startTime' | 'endTime' | 'duration' | 'comment' | 'file';
type FilterOperator = 'contains' | 'equals' | 'startsWith' | 'endsWith' | 'isEmpty' | 'isNotEmpty';

interface FilterRule {
    id: number;
    mode: FilterMode;
    field: FilterField;
    operator: FilterOperator;
    value: string;
}

const FILTER_FIELD_OPTIONS: Array<{ value: FilterField; label: string }> = [
    { value: 'machine', label: 'Machine' },
    { value: 'alias', label: 'Alias' },
    { value: 'catalogueCode', label: 'Catalogue Code' },
    { value: 'reason', label: 'Stop Reason' },
    { value: 'mwc', label: 'MWC' },
    { value: 'startTime', label: 'Start Time' },
    { value: 'endTime', label: 'End Time' },
    { value: 'duration', label: 'Duration' },
    { value: 'comment', label: 'Comment' },
    { value: 'file', label: 'File' },
];

const FILTER_OPERATOR_OPTIONS: Array<{ value: FilterOperator; label: string }> = [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'equals' },
    { value: 'startsWith', label: 'starts with' },
    { value: 'endsWith', label: 'ends with' },
    { value: 'isEmpty', label: 'is empty' },
    { value: 'isNotEmpty', label: 'is not empty' },
];

const createFilterRule = (id: number): FilterRule => ({
    id,
    mode: 'include',
    field: 'reason',
    operator: 'contains',
    value: '',
});

const formatStopDate = (value: Date | string | number | null | undefined) => {
    if (!value) return 'N/A';

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';

    return new Intl.DateTimeFormat('sv-SE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date);
};

const formatStopDuration = (duration: StopCause['Duration']) => {
    if (!duration) return 'N/A';

    const { days = 0, hours = 0, minutes = 0, seconds = 0 } = duration;
    const parts = [
        days > 0 ? `${days}d` : null,
        hours > 0 ? `${hours}h` : null,
        minutes > 0 ? `${minutes}m` : null,
        seconds > 0 ? `${seconds}s` : null,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(' ') : '0s';
};

const getStopAttachedFileIds = (stop: StopCause) => {
    return (stop.AttachedFileIds ?? []).filter(Boolean);
};

const hasStopFiles = (stop: StopCause) => {
    return stop.HasImage || getStopAttachedFileIds(stop).length > 0;
};

const matchesFileFilterRule = (stop: StopCause, rule: FilterRule) => {
    const fileIds = getStopAttachedFileIds(stop);
    const hasFiles = stop.HasImage || fileIds.length > 0;
    const filterValue = rule.value.trim().toLocaleLowerCase();
    const candidate = ['yes', stop.HasImage ? 'image' : '', ...fileIds]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase();

    switch (rule.operator) {
        case 'contains':
            return candidate.includes(filterValue);
        case 'equals':
            return filterValue === 'yes'
                ? hasFiles
                : filterValue === 'no'
                    ? !hasFiles
                    : candidate === filterValue;
        case 'startsWith':
            return candidate.startsWith(filterValue);
        case 'endsWith':
            return candidate.endsWith(filterValue);
        case 'isEmpty':
            return !hasFiles;
        case 'isNotEmpty':
            return hasFiles;
        default:
            return true;
    }
};

const getStopFieldValue = (stop: StopCause, field: FilterField) => {
    switch (field) {
        case 'machine':
            return stop.MachineId || '';
        case 'alias':
            return stop.Alias || '';
        case 'catalogueCode':
            return stop.CatalogueCode || '';
        case 'reason':
            return normalizeStopCodingReason(stop.MachineStopReason);
        case 'mwc':
            return stop.MWC || '';
        case 'startTime':
            return formatStopDate(stop.StartTime);
        case 'endTime':
            return formatStopDate(stop.EndTime);
        case 'duration':
            return formatStopDuration(stop.Duration);
        case 'comment':
            return stop.Comment || '';
        case 'file':
            return hasStopFiles(stop) ? 'Yes' : '';
        default:
            return '';
    }
};

export default function StopCodingTableList() {
    const allStopCauses = useStopCodingSelector((state) => state.allStopCauses);
    const startDate = useStopCodingSelector((state) => state.startDate);
    const endDate = useStopCodingSelector((state) => state.endDate);
    const stopCodingFilterDisplayValue = useStopCodingSelector((state) => state.stopCodingFilterDisplayValue);
    const cascadeReasonVisibility = useStopCodingSelector((state) => state.cascadeReasonVisibility);
    const selectedCascadeReason = useStopCodingSelector((state) => state.selectedCascadeReason);
    const [filterRules, setFilterRules] = useState<FilterRule[]>([]);
    const nextRuleIdRef = useRef(2);

    const cascadeScopedStops = useMemo(() => {
        return filterStopCausesForCascade(allStopCauses, startDate, endDate);
    }, [allStopCauses, endDate, startDate]);

    const visibleStops = useMemo(() => {
        return cascadeScopedStops.filter((stop) => {
            const reason = normalizeStopCodingReason(stop.MachineStopReason);
            const matchesSelectedReason = isStopReasonInCascadePath(reason, selectedCascadeReason);

            if (!matchesSelectedReason) {
                return false;
            }

            const visibleReasonKey = getStopReasonDrilldownGroupPath(reason, selectedCascadeReason);
            const isVisibleReason = !visibleReasonKey || cascadeReasonVisibility[visibleReasonKey] !== false;

            return isVisibleReason && matchesSelectedReason;
        });
    }, [cascadeReasonVisibility, cascadeScopedStops, selectedCascadeReason]);

    const activeFilterRules = useMemo(() => {
        return filterRules.filter((rule) => {
            if (rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty') {
                return true;
            }

            return rule.value.trim().length > 0;
        });
    }, [filterRules]);

    const filteredStops = useMemo(() => {
        if (activeFilterRules.length === 0) {
            return visibleStops;
        }

        return visibleStops.filter((stop) => {
            return activeFilterRules.every((rule) => {
                if (rule.field === 'file') {
                    const matches = matchesFileFilterRule(stop, rule);
                    return rule.mode === 'include' ? matches : !matches;
                }

                const candidate = getStopFieldValue(stop, rule.field).trim().toLocaleLowerCase();
                const filterValue = rule.value.trim().toLocaleLowerCase();

                let matches = false;
                switch (rule.operator) {
                    case 'contains':
                        matches = candidate.includes(filterValue);
                        break;
                    case 'equals':
                        matches = candidate === filterValue;
                        break;
                    case 'startsWith':
                        matches = candidate.startsWith(filterValue);
                        break;
                    case 'endsWith':
                        matches = candidate.endsWith(filterValue);
                        break;
                    case 'isEmpty':
                        matches = candidate.length === 0 || candidate === 'n/a' || candidate === '-';
                        break;
                    case 'isNotEmpty':
                        matches = !(candidate.length === 0 || candidate === 'n/a' || candidate === '-');
                        break;
                    default:
                        matches = true;
                        break;
                }

                return rule.mode === 'include' ? matches : !matches;
            });
        });
    }, [activeFilterRules, visibleStops]);

    const visibleReasonCount = useMemo(() => {
        return new Set(filteredStops.map((stop) => normalizeStopCodingReason(stop.MachineStopReason))).size;
    }, [filteredStops]);

    const updateFilterRule = (ruleId: number, updates: Partial<FilterRule>) => {
        setFilterRules((currentRules) =>
            currentRules.map((rule) => (rule.id === ruleId ? { ...rule, ...updates } : rule))
        );
    };

    const addFilterRule = () => {
        const nextId = nextRuleIdRef.current;
        nextRuleIdRef.current += 1;
        setFilterRules((currentRules) => [...currentRules, createFilterRule(nextId)]);
    };

    const removeFilterRule = (ruleId: number) => {
        setFilterRules((currentRules) => currentRules.filter((rule) => rule.id !== ruleId));
    };

    const clearFilterRules = () => {
        setFilterRules([]);
    };

    const selectedFilterLabel = stopCodingFilterDisplayValue || 'All';
    const reasonLabel = selectedCascadeReason ? ` DRILLDOWN: ${getStopReasonSelectionPath(selectedCascadeReason)}` : '';
    const title = `STOP TABLE LIST TOTAL STOPS: ${filteredStops.length} REASONS: ${visibleReasonCount} FILTER: ${selectedFilterLabel}${reasonLabel}`;

    const headerContent = (
        <div className="overflow-hidden  text-primary-dark shadow-sm dark:  dark:text-primary-light p-4" >
            <div className="">
                {filterRules.map((rule, index) => {
                    const isValueOptional = rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty';

                    return (
                        <div
                            key={rule.id}
                            className="mb-2"
                        >


                            <div className="grid gap-3 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_auto] lg:items-end">


                                <label className="flex flex-col text-primary-dark dark:text-primary-light text-sm gap-1">
                                    Mode
                                    <CustomSelect
                                        className="h-9"
                                        value={rule.mode}
                                        onChange={(e) =>
                                            updateFilterRule(rule.id, { mode: e.target.value as FilterMode })
                                        }
                                    >
                                        <option value="include">Include</option>
                                        <option value="exclude">Exclude</option>
                                    </CustomSelect>
                                </label>


                                <label className="flex flex-col text-primary-dark dark:text-primary-light text-sm gap-1">
                                    Header
                                    <CustomSelect
                                        className="h-9"
                                        value={rule.field}
                                        onChange={(e) =>
                                            updateFilterRule(rule.id, { field: e.target.value as FilterField })
                                        }
                                    >
                                        {FILTER_FIELD_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </CustomSelect>
                                </label>


                                <label className="flex flex-col text-primary-dark dark:text-primary-light text-sm gap-1">
                                    Match
                                    <CustomSelect
                                        className="h-9"
                                        value={rule.operator}
                                        onChange={(e) =>
                                            updateFilterRule(rule.id, {
                                                operator: e.target.value as FilterOperator,
                                            })
                                        }
                                    >
                                        {FILTER_OPERATOR_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </CustomSelect>
                                </label>

                                <label className="flex flex-col text-primary-dark dark:text-primary-light text-sm gap-1">
                                    Value
                                    <input
                                        type="text"
                                        value={rule.value}
                                        onChange={(e) =>
                                            updateFilterRule(rule.id, { value: e.target.value })
                                        }
                                        disabled={isValueOptional}
                                        placeholder={isValueOptional ? "No value needed" : "Enter a value to match"}
                                        className="text-left text-xs h-9
          truncate w-full px-4 py-2 bg-primary-light dark:bg-primary-dark text-primary-dark dark:text-primary-light outline-none"
                                    />
                                </label>

                                <button
                                    type="button"
                                    onClick={() => removeFilterRule(rule.id)}
                                    aria-label={`Remove filter rule ${index + 1}`}
                                    className="self-start lg:self-auto px-3 py-2 text-red-500 hover:text-red-700"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    );
                })}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4">
                    <div className="text-sm text-primary-dark dark:text-primary-light">

                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Button appearance="outline" icon={<Add20Regular />} onClick={addFilterRule} style={{ borderRadius: 0 }}>
                            Add rule
                        </Button>
                        {filterRules.length > 0 && (
                            <Button appearance="subtle" onClick={clearFilterRules} disabled={filterRules.length === 0} style={{ borderRadius: 0 }}>
                                Clear rules
                            </Button>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );

    return <StopCodingList stopCauses={filteredStops} title={title} headerContent={headerContent} />;
}